import { ActionTree, Module, MutationTree } from 'vuex'

import RepoMemorySyncStatusQuery from '@/apollo/queries/repository/knowledge/memorySyncStatus.gql'
import { RootState } from '~/store'
import { VcsProviderChoices } from '~/types/types'

export enum KnowledgeSyncMutations {
  SET_SYNC_STATUS = 'setSyncStatus',
  SET_LOADING = 'setLoading',
  SET_ERROR = 'setError'
}

export interface KnowledgeSyncModuleState {
  lastSyncedAt: string | null
  loading: boolean
  error: string | null
}

export interface KnowledgeSyncFetchArgs {
  provider: VcsProviderChoices
  owner: string
  name: string
}

export interface KnowledgeSyncModuleMutations extends MutationTree<KnowledgeSyncModuleState> {
  [KnowledgeSyncMutations.SET_SYNC_STATUS]: (
    state: KnowledgeSyncModuleState,
    lastSyncedAt: string | null
  ) => void
  [KnowledgeSyncMutations.SET_LOADING]: (state: KnowledgeSyncModuleState, value: boolean) => void
  [KnowledgeSyncMutations.SET_ERROR]: (
    state: KnowledgeSyncModuleState,
    value: string | null
  ) => void
}

const knowledgeSyncStoreModule: Module<KnowledgeSyncModuleState, RootState> = {
  namespaced: true,
  state: (): KnowledgeSyncModuleState => ({
    lastSyncedAt: null,
    loading: false,
    error: null
  }),
  mutations: {
    [KnowledgeSyncMutations.SET_SYNC_STATUS](state, lastSyncedAt) {
      state.lastSyncedAt = lastSyncedAt
    },
    [KnowledgeSyncMutations.SET_LOADING](state, value) {
      state.loading = value
    },
    [KnowledgeSyncMutations.SET_ERROR](state, value) {
      state.error = value
    }
  } as KnowledgeSyncModuleMutations,
  actions: {
    async fetchSyncStatus({ commit }, { provider, owner, name }: KnowledgeSyncFetchArgs) {
      commit(KnowledgeSyncMutations.SET_LOADING, true)
      commit(KnowledgeSyncMutations.SET_ERROR, null)
      try {
        const response = await this.$fetchGraphqlData(
          RepoMemorySyncStatusQuery,
          {
            provider,
            owner,
            name
          },
          true
        )
        const status = response?.data?.repository?.memorySyncStatus
        commit(KnowledgeSyncMutations.SET_SYNC_STATUS, status?.lastSyncedAt ?? null)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch sync status'
        commit(KnowledgeSyncMutations.SET_ERROR, message)
        throw err
      } finally {
        commit(KnowledgeSyncMutations.SET_LOADING, false)
      }
    }
  } as ActionTree<KnowledgeSyncModuleState, RootState>
}

export default knowledgeSyncStoreModule
